import { formatDate } from "@lib/intl"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import type { CutNoun } from "@/types"

/**
 * How an evaluation cut is written, everywhere (PRD-61).
 *
 * Five surfaces name a cut — the roster, the material, the student history,
 * the classroom card and Home — so the wording lives once, here, rather than
 * as five hand-built "Parcial 2 · 27 sep – 31 oct" strings that drift.
 *
 * The backend also sends a Spanish `label` ("Parcial 2"). Every cut payload
 * carries its calendar's `noun`, so the label is rebuilt through i18n and
 * en-US reads in English; the server's label is only the fallback for a
 * payload cached from before the noun existed.
 */
export function useCutFormat() {
  const { t } = useTranslation("common")
  return useMemo(() => {
    // Short day + month, ordered by locale: "31 oct" / "Oct 31".
    const day = (iso: string) => formatDate(iso, t("cuts.day_format"))
    return {
      label: (cut: { number: number; label: string; noun?: CutNoun | null }) =>
        cut.noun ? t(`cuts.label.${cut.noun}`, { number: cut.number }) : cut.label,
      range: (start: string, end: string) => t("cuts.range", { start: day(start), end: day(end) }),
      closes: (end: string) => t("cuts.closes", { date: day(end) }),
      wholeCourse: () => t("cuts.whole_course"),
    }
  }, [t])
}
